import { Helmet, HelmetData } from "react-helmet-async";
import { useRef } from "react";
import {
  AI_BG_NEW,
  API_Development_Img,
  Clear_Documentation_Icon,
  Deployment_Monitoring_Icon,
  Development_Testing_Icon,
  Documentation_Support_Icon,
  Enterprise_Grade_Icon,
  Integration_Discovery_Icon,
  Making_AI_Work_Img,
  Performance_Optimization_Icon,
  Realtime_Integration_Img,
  Scalable_Integration_Hero_Img,
  Security_First_Icon,
  Services_Bg,
  Use_Case_Bg,
  Workflow_Auto_Img,
} from "../../images";
import BookConsultation from "../book-a-consultation";
import { NewFooter } from "../newFooter";
import useIntersectionObserver from "../../utils/useIntersectionObserver";

export default function ScalableIntegration() {
  const targetRef = useRef(null);

  useIntersectionObserver({ targetRef });

  const helmetData = new HelmetData({});

  const services = [
    {
      id: 1,
      title: "API Development & Integration",
      description:
        "Custom API design that connects AI models to your applications, databases, and third-party services. We build RESTful and GraphQL APIs optimised for AI.",
      image: API_Development_Img,
    },
    {
      id: 2,
      title: "Workflow Automation",
      description:
        " AI-powered automation that integrates into your existing business processes, from document processing to customer service.",
      image: Workflow_Auto_Img,
    },
    {
      id: 3,
      title: "Real-Time Data Integration",
      description:
        "Streaming data pipelines that enable real-time AI inference and decision-making with low-latency performance.",
      image: Realtime_Integration_Img,
    },
  ];

  const features = [
    {
      icon: Integration_Discovery_Icon,
      title: "Integration Discovery",
      description:
        "We map your system architecture, identify integration points, and design scalable solutions that align fully with your infrastructure requirements.",
    },
    {
      icon: Development_Testing_Icon,
      title: "Development & Testing",
      description:
        "We use agile development with continuous testing in staging environments that mirror production, ensuring integrations function reliably before deployment.",
    },
    {
      icon: Deployment_Monitoring_Icon,
      title: "Deployment & Monitoring",
      description:
        "We manage a staged rollout with comprehensive monitoring to verify performance, stability, and real-world reliability across your operational systems.",
    },
    {
      icon: Documentation_Support_Icon,
      title: "Documentation & Support",
      description:
        "We provide complete technical documentation and ongoing support as systems evolve, ensuring your integrations remain clear, maintainable, and future-ready.",
    },
  ];

  const why = [
    {
      icon: Enterprise_Grade_Icon,
      title: "Enterprise-Grade Reliability",
      text: "Integrations built with proper error handling, retry logic, monitoring, and failover capabilities.",
    },
    {
      icon: Performance_Optimization_Icon,
      title: "Performance Optimization",
      text: " Designs that minimize latency and maximize throughput, ensuring AI capabilities don't slow down your operations.",
    },
    {
      icon: Security_First_Icon,
      title: "Security First",
      text: "Encrypted data transmission, secure credential management, and compliance with your security policies.",
    },
    {
      icon: Clear_Documentation_Icon,
      title: "Clear Documentation",
      text: "Every integration includes API documentation, architecture diagrams, and runbooks. ",
    },
  ];

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>Scalable AI Integrations Africa | Enterprise AI Integration | Analytics Intelligence Africa</title>

        <link
          rel="canonical"
          href="https://africa.analyticsintelligence.com/scalable-integrations"
        />

        <meta
          name="description"
          content="Analytics Intelligence Africa builds scalable, reliable AI integrations that connect AI models with enterprise systems and workflows. Seamlessly embed AI into your existing infrastructure — API development, workflow automation, and real-time data integration for African enterprises."
        />

        <meta
          name="keywords"
          content="Analytics Intelligence Africa, scalable AI integrations Africa, enterprise AI integration Africa, AI system integration Africa, API development AI Africa, workflow automation AI Africa, real-time AI integration Africa, AI infrastructure Africa, MLOps integration Africa, AI deployment Africa, AI for banking Africa, AI for fintech Africa"
        />

        <meta property="og:title" content="Scalable AI Integrations | Enterprise AI Integration Africa | Analytics Intelligence Africa" />
        <meta property="og:description" content="Reliable, enterprise-grade AI integrations that connect models, data, and workflows for scalable AI adoption across Africa." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://africa.analyticsintelligence.com/scalable-integrations" />
        <meta property="og:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Scalable AI Integrations | Enterprise AI Integration Africa | Analytics Intelligence Africa" />
        <meta name="twitter:description" content="Reliable, enterprise-grade AI integrations that connect models, data, and workflows for scalable AI adoption across Africa." />
        <meta name="twitter:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Scalable AI Integrations",
            "url": "https://africa.analyticsintelligence.com/scalable-integrations",
            "description": "Building scalable, reliable AI integrations that connect AI models with enterprise systems — API development, workflow automation, and real-time data integration for African enterprises.",
            "provider": {
              "@type": "Organization",
              "name": "Analytics Intelligence Africa",
              "url": "https://africa.analyticsintelligence.com"
            },
            "areaServed": ["Nigeria", "Ghana", "Kenya", "South Africa", "Rwanda", "Africa"]
          }
        `}</script>
      </Helmet>

      <main>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundSize: "cover",
          }}
          className="min-h-[100vh] flex items-center bg-left-bottom md:bg-center"
          ref={targetRef}
        >
          <div className="container mx-auto w-full h-full flex flex-col md:flex-row justify-between items-center gap-16 md:gap-20 lg:gap-28 xl:gap-36  pt-[126px] pb-11 md:py-[96px] py-[96px]">
            <div className="flex basis-2/4 flex-col flex-wrap gap-[16px] items-center md:items-start">
              <div className="space-y-8 md:space-y-11 flex flex-col items-center md:items-start">
                <span className="text-sm md:text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                  Scalable Integration
                </span>
                <div className="space-y-4">
                  <h2 className="text-4xl md:text-[40px] xl:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[54px] text-white">
                    Seamless AI Integrations <br className="hidden xl:block" />{" "}
                    That Scale
                  </h2>
                  <p className="text-sm md:text-lg text-center md:text-left font-medium text-[#E3E3E3] leading-5">
                    Connecting your AI capabilities with existing systems and
                    workflows through reliable, efficient integrations built for
                    enterprise scale 
                  </p>
                </div>
              </div>
            </div>
            <div className="basis-2/4 flex justify-end">
              <img
                className=" w-full max-w-[680px]"
                src={Scalable_Integration_Hero_Img}
                alt="illustration"
              />
            </div>
          </div>
        </section>

        <section className="relative w-full bg-white py-[80px] md:py-[100px] overflow-hidden">
          <div className="mx-auto container">
            <div className="h-[300px] w-[300px] xl:h-[400px] xl:w-[400px] bg-[#7AA8FF] blur-[190px] absolute top-0 -right-[70%] md:-right-[45%] lg:-right-[15%]"></div>
            <div className="flex flex-col items-center gap-8 md:grid md:gap-12 lg:gap[65px] xl:gap-[88px] md:grid-cols-2">
              <div className="space-y-4">
                <h2 className="text-2xl xl:text-[32px] font-bold !leading-[120%]">
                  Making AI Work Within Your Ecosystem
                </h2>
                <p className="text-base md:text-lg xl:text-xl font-medium text-[#333333]">
                  We specialise in building robust, scalable integrations that
                  connect AI models with your enterprise infrastructure,
                  ensuring smooth data flow and seamless user experiences. Our
                  integration specialists bridge the gap between AI technologies
                  and enterprise systems effectively.
                </p>
              </div>

              <img
                src={Making_AI_Work_Img}
                alt="computer illustration"
                className="h-auto w-full max-w-[640px] object-cover"
              />
            </div>
          </div>
        </section>

        <section
          className="relative py-20 bg-left-bottom md:bg-center bg-cover md:bg-cover"
          style={{
            backgroundImage: `url("${Services_Bg}")`,
          }}
        >
          <div className="container mx-auto">
            {/* Header */}
            <div className="mb-8 text-center">
              <h1 className="mb-2 text-2xl md:text-3xl font-bold tracking-tight xl:text-[36px] text-[#FFF]">
                Our Core Services
              </h1>
            </div>

            {/* Service Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-y-6 md:gap-8">
              {services.map((service) => (
                <div
                  key={service.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 col-span-1 md:col-span-6 lg:col-span-4"
                >
                  {/* Image Container */}
                  <div className="relative h-[240px] md:h-72 flex items-center justify-center p-6">
                    <img
                      src={service.image || "/placeholder.svg"}
                      alt={service.title}
                      className="w-full h-full object-contain"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-lg md:text-2xl font-bold mb-2 md:mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative w-full bg-white py-[80px] md:py-[100px] overflow-hidden">
          <div className="mx-auto container">
            <div className="h-[250px] w-[250px] xl:h-[300px] xl:w-[300px] bg-[#7AA8FF] blur-[190px] absolute top-0 -right-[70%] md:-right-[45%] lg:-right-[15%]"></div>
            <div className="mb-16 md:mb-20 text-center">
              <h1 className="text-2xl font-bold tracking-tight md:text-[36px]">
                Our Process
              </h1>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-8 md:gap-y-12 md:gap-x-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="relative flex flex-col items-center text-center bg-[#EDF9FF29] border border-[#EDF9FF] px-6 py-[32px]"
                >
                  {/* Icon */}
                  <img
                    src={feature.icon}
                    alt="icon"
                    className="absolute -top-5"
                  />

                  {/* Title */}
                  <h3 className="text-lg font-bold mb-2">{feature.title}</h3>

                  {/* Description */}
                  <p className="text-base">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="bg-[#EDF1FF] py-[48px] bg-left-bottom md:bg-center bg-cover md:bg-cover"
          style={{
            backgroundImage: `url("${Use_Case_Bg}")`,
          }}
        >
          <div className="container space-y-12">
            <div className="mb-10 text-center">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight xl:text-[36px] text-[#FFF]">
                Why Partners Choose us
              </h1>
            </div>

            <div className="grid grid-cols-12 gap-y-8 sm:gap-8">
              {why.map((item, idx) => (
                <div
                  key={idx}
                  className="col-span-12 sm:col-span-6 xl:col-span-3 bg-white flex flex-col items-center rounded-xl px-3 py-6 gap-4"
                >
                  <img
                    src={item.icon}
                    alt={`${item.title} illustration`}
                    className="w-16 h-16"
                  />
                  <div className="space-y-2 text-center">
                    <h5 className="text-lg md:text-xl font-bold text-[#020128]">
                      {item.title}
                    </h5>
                    <p className="text-sm md:text-base text-[#020128]">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <NewFooter>
          <BookConsultation
            title="Start Your AI Integration Journey With Us"
            text="Let's discuss your integration challenges and design solutions that make AI a seamless part of your operations."
            btnText="Contact Us"
            href="/contact"
          />
        </NewFooter>
      </main>
    </>
  );
}
